'use client';

import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useAuth } from '@/context/AuthContext';
import { getMissingSupabaseBrowserEnv } from '@/lib/supabase/env';

type Props = {
  mode: 'sign-in' | 'sign-up';
};

export default function AuthCard({ mode }: Props) {
  const { signIn, signUp, user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [missingEnv, setMissingEnv] = useState<string[] | null>(null);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isSignUp = mode === 'sign-up';

  useEffect(() => {
    setMissingEnv(getMissingSupabaseBrowserEnv());
  }, []);

  useEffect(() => {
    if (user) {
      const nextPath = searchParams.get('next') || '/account';
      router.replace(nextPath);
    }
  }, [router, searchParams, user]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      if (isSignUp) {
        await signUp({
          email,
          fullName,
          phone,
          nextPath: searchParams.get('next') || '/account',
        });
        setSuccess(
          'Check your email to confirm your account. Once confirmed, the magic link will sign you in.'
        );
      } else {
        await signIn({
          email,
          nextPath: searchParams.get('next') || '/account',
        });
        setSuccess('Check your email for your sign-in magic link.');
      }
    } catch (submitError) {
      const message = submitError instanceof Error ? submitError.message : 'Authentication failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md rounded-2xl border border-border bg-white p-6 shadow-sm">
      <h1 className="font-heading text-2xl font-bold text-primary">
        {isSignUp ? 'Create your account' : 'Sign in'}
      </h1>
      <p className="mt-2 text-sm text-foreground/70">
        {isSignUp
          ? 'Create your account with a secure email magic link.'
          : 'Sign in with a secure email magic link.'}
      </p>
      {missingEnv && missingEnv.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Authentication is unavailable. Missing Supabase configuration:
          <strong> {missingEnv.join(', ')}</strong>
        </div>
      )}
      <form onSubmit={handleSubmit} className="mt-5 space-y-4">
        {isSignUp && (
          <>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-foreground">Full name</span>
              <input
                required
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-foreground">Phone</span>
              <input
                required
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                className="w-full rounded-lg border border-border px-3 py-2"
              />
            </label>
          </>
        )}
        <label className="block text-sm">
          <span className="mb-1 block font-medium text-foreground">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="w-full rounded-lg border border-border px-3 py-2"
          />
        </label>
        {error && (
          <p aria-live="assertive" className="text-sm text-red-600">
            {error}
          </p>
        )}
        {success && (
          <p aria-live="polite" className="text-sm text-green-700">
            {success}
          </p>
        )}
        <button
          disabled={submitting || (missingEnv?.length ?? 0) > 0}
          type="submit"
          className="w-full rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        >
          {submitting ? 'Please wait...' : isSignUp ? 'Send sign-up link' : 'Send sign-in link'}
        </button>
      </form>
    </div>
  );
}
